import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Quote, Plus, Loader2, X, Send } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { usePagesContent } from "@/data/pages";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ReviewsSection = () => {
  const { reviews: header } = usePagesContent();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API}/reviews`);
      setReviews(res.data || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  const handleNewReview = (review) => {
    setReviews((r) => [review, ...r]);
    setOpen(false);
  };

  const avgRating =
    reviews.length > 0
      ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
      : null;

  return (
    <section
      id="recensioni"
      data-testid="reviews-section"
      className="relative py-24 lg:py-32 bg-[#F1E4C9] overflow-hidden"
    >
      <div className="absolute top-0 right-0 w-[35vmin] h-[35vmin] rounded-full bg-[#7AB8D6]/25 blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 left-[-5%] w-[28vmin] h-[28vmin] rounded-full bg-[#A0522D]/15 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl mb-12"
        >
          <p className="overline text-[#4F8FA8] mb-5">{header.eyebrow}</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#2A1810]">
            {header.heading_line1}
            <br />
            <span className="font-serif-italic text-[#A0522D]">{header.heading_italic}</span>
          </h2>
          {header.description && (
            <p className="mt-5 text-[#6E5B4A] text-base sm:text-lg max-w-xl leading-relaxed">
              {header.description}
            </p>
          )}
          {avgRating && (
            <div className="mt-6 flex items-center gap-3">
              <div className="flex">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star
                    key={i}
                    size={20}
                    className={
                      i <= Math.round(avgRating)
                        ? "fill-[#A0522D] text-[#A0522D]"
                        : "text-[#D9C5A4]"
                    }
                  />
                ))}
              </div>
              <span className="text-[#2A1810] font-serif-display text-xl">
                {avgRating}
              </span>
              <span className="text-[#6E5B4A] text-sm">
                ({reviews.length} recension{reviews.length === 1 ? "e" : "i"})
              </span>
            </div>
          )}
        </motion.div>

        {loading ? (
          <div className="text-center py-16 text-[#6E5B4A]">
            <Loader2 size={28} className="animate-spin mx-auto" />
          </div>
        ) : reviews.length === 0 ? (
          <div className="bg-[#FBF6EA] border border-dashed border-[#D9C5A4] rounded-md p-12 text-center mb-10">
            <Quote size={28} className="mx-auto text-[#A0522D] mb-4" />
            <p className="font-serif-display text-2xl text-[#2A1810]">
              Sii la prima a raccontare la tua esperienza
            </p>
            <p className="text-[#6E5B4A] mt-2 text-sm">
              La tua voce farà conoscere la Summer Heels Experience a chi è
              curioso di provare.
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
            {reviews.slice(0, 9).map((r, idx) => (
              <ReviewCard key={r.id} review={r} idx={idx} />
            ))}
          </div>
        )}

        <div className="flex justify-center">
          <button
            data-testid="open-review-form"
            onClick={() => setOpen(true)}
            className="btn-azzurro"
          >
            <Plus size={16} /> Lascia una recensione
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <ReviewFormModal
            onClose={() => setOpen(false)}
            onCreated={handleNewReview}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

const ReviewCard = ({ review, idx }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: idx * 0.06 }}
    className="bg-[#FBF6EA] border border-[#D9C5A4] rounded-md p-6 relative shadow-[0_10px_24px_-18px_rgba(160,82,45,0.3)]"
  >
    <Quote
      size={26}
      className="text-[#A0522D]/35 absolute top-5 right-5"
      strokeWidth={1.5}
    />
    <div className="flex gap-0.5 mb-4">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={14}
          className={
            i <= review.rating
              ? "fill-[#A0522D] text-[#A0522D]"
              : "text-[#D9C5A4]"
          }
        />
      ))}
    </div>
    <p className="text-[#2A1810] text-sm leading-relaxed mb-5 italic font-serif-italic">
      &ldquo;{review.text}&rdquo;
    </p>
    <div className="border-t border-[#D9C5A4] pt-3">
      <p className="font-serif-display text-lg text-[#2A1810]">{review.name}</p>
      <p className="overline text-[#6E5B4A] mt-1">
        {new Date(review.created_at).toLocaleDateString("it-IT", {
          month: "long",
          year: "numeric",
        })}
      </p>
    </div>
  </motion.div>
);

const ReviewFormModal = ({ onClose, onCreated }) => {
  const [name, setName] = useState("");
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const canSubmit =
    name.trim().length >= 2 && rating >= 1 && text.trim().length >= 10;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/reviews`, {
        name: name.trim(),
        rating,
        text: text.trim(),
      });
      toast.success("Grazie per la tua recensione ✨");
      onCreated(res.data);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail?.[0]?.msg || "Errore. Riprova."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-[100] bg-[#4F8FA8]/40 backdrop-blur-md flex items-center justify-center p-6 overflow-y-auto"
    >
      <motion.form
        initial={{ opacity: 0, scale: 0.96, y: 30 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 30 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        data-testid="review-form-modal"
        className="relative bg-[#FBF6EA] border border-[#D9C5A4] rounded-md max-w-md w-full p-8 shadow-2xl"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-[#6E5B4A] hover:text-[#A0522D] p-2 transition"
          aria-label="chiudi"
        >
          <X size={20} />
        </button>

        <p className="overline text-[#4F8FA8] mb-2">La tua voce</p>
        <h3 className="font-serif-display text-3xl text-[#2A1810]">
          Lascia una recensione
        </h3>
        <p className="text-[#6E5B4A] text-sm mt-3">
          Aiuta chi sta pensando di iscriversi raccontando la tua esperienza.
        </p>

        <div className="mt-7 space-y-5">
          <div>
            <label className="overline text-[#6E5B4A] block mb-2">Nome</label>
            <input
              data-testid="review-name"
              type="text"
              required
              minLength={2}
              maxLength={80}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input-warm"
              placeholder="Maria R."
            />
          </div>

          <div>
            <label className="overline text-[#6E5B4A] block mb-2">
              Valutazione
            </label>
            <div className="flex gap-1" data-testid="rating-stars">
              {[1, 2, 3, 4, 5].map((i) => (
                <button
                  key={i}
                  type="button"
                  data-testid={`star-${i}`}
                  onClick={() => setRating(i)}
                  onMouseEnter={() => setHover(i)}
                  onMouseLeave={() => setHover(0)}
                  className="p-1 transition hover:scale-110"
                  aria-label={`${i} stelle`}
                >
                  <Star
                    size={28}
                    className={
                      i <= (hover || rating)
                        ? "fill-[#A0522D] text-[#A0522D]"
                        : "text-[#D9C5A4]"
                    }
                  />
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="overline text-[#6E5B4A] block mb-2">
              La tua recensione
            </label>
            <textarea
              data-testid="review-text"
              required
              minLength={10}
              maxLength={600}
              rows={5}
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="input-warm resize-none"
              placeholder="Racconta com'è stata la tua esperienza con Alessandro…"
            />
            <p className="text-xs text-[#6E5B4A] text-right mt-1">
              {text.length}/600
            </p>
          </div>
        </div>

        <button
          type="submit"
          data-testid="review-submit"
          disabled={!canSubmit || submitting}
          className="btn-primary w-full justify-center mt-6 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {submitting ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <>
              <Send size={14} /> Pubblica recensione
            </>
          )}
        </button>
      </motion.form>
    </motion.div>
  );
};
